import { LightningIcon } from '@components/Icons/LightningIcon';
import { LightningOffIcon } from '@components/Icons/LightningOffIcon';
import { MobileDrawer } from '@components/MobileDrawer/MobileDrawer';
import classNames from 'classnames';
import { useState } from 'react';
import css from './LightningAutoplayButton.module.scss';

type LightningAutoplayButtonProps = {
  autoplay: boolean;
  onSelect: (enabled: boolean) => void;
  type: 'moments' | 'videos';
};

/**
 * The mobile autoplay control: a frosted circle showing the current autoplay
 * state (lightning on/off) that opens a bottom drawer to change it. Shared by
 * the Search page (moments) and the per-library page (media) so the two
 * surfaces can't drift.
 */
export const LightningAutoplayButton = ({
  autoplay,
  onSelect,
  type,
}: LightningAutoplayButtonProps) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const close = () => setDrawerOpen(false);

  const label = type === 'moments' ? 'moments' : 'media';
  const action = type === 'moments' ? 'moment' : 'media item';

  const options = [
    {
      enabled: true,
      icon: <LightningIcon />,
      title: `Autoplay ${label}`,
      description: `Play ${label} when centered.`,
    },
    {
      enabled: false,
      icon: <LightningOffIcon />,
      title: `Don't autoplay ${label}`,
      description: `Tap a ${action} to view details.`,
    },
  ];

  return (
    <>
      <button
        className={css.button}
        onClick={() => setDrawerOpen(true)}
        aria-haspopup="true"
        aria-expanded={drawerOpen}
        aria-label={autoplay ? `Autoplay ${label}: on` : `Autoplay ${label}: off`}
      >
        {autoplay ? <LightningIcon /> : <LightningOffIcon />}
      </button>

      <MobileDrawer open={drawerOpen} onClose={close} onOpen={() => setDrawerOpen(true)} frosted>
        <div className={css.drawerContent}>
          <span className={css.title}>Autoplay</span>
          <div className={css.options} role="menu">
            {options.map((option) => (
              <button
                key={String(option.enabled)}
                role="menuitemradio"
                aria-checked={autoplay === option.enabled}
                className={classNames(css.option, {
                  [css.optionActive]: autoplay === option.enabled,
                })}
                onClick={() => {
                  onSelect(option.enabled);
                  close();
                }}
              >
                {option.icon}
                <div className={css.optionText}>
                  <span className={css.optionTitle}>{option.title}</span>
                  <span className={css.optionDescription}>{option.description}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </MobileDrawer>
    </>
  );
};
